/**
 * The video for one candidate on one role: the icon on that workstation row,
 * and the three dialogs the design file gives it.
 *
 *   no video yet, or the last one failed   "Upload Dossier"
 *   a video is being made                  "Request submitted"
 *   the video is ready                     "Your video is ready" + Download
 *
 * Layout, colours and wording are lifted from SAKETH-KB/DESIGN rather than
 * approximated. The same candidate on another role has its own video.
 *
 * Opening asks Node for this row's latest video. That same request is what
 * brings a finished video from ai-ml into PINE, so the grid needs no polling:
 * "click the video icon again to download it" is the whole mechanism.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import toastr from "../../../utils/toast";

const ORANGE = "#f26b2a";
const ORANGE_DARK = "#d95a1d";
const MAX_BYTES = 20 * 1024 * 1024;

const svg = (size, children, extra = {}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...extra}
  >
    {children}
  </svg>
);

const IconVideo = ({ size }) =>
  svg(
    size,
    <>
      <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" />
      <rect x="2" y="6" width="14" height="12" rx="2" />
    </>,
  );

const IconTrash = () => (
  <svg width="14" height="16" viewBox="0 0 14 16" fill="currentColor">
    <path d="M4.5 0h5l.6 1.2H14v2H0v-2h3.9zM1 4h12l-.9 11.1c-.1.5-.5.9-1 .9H2.9c-.5 0-.9-.4-1-.9z" />
  </svg>
);

/** A button with the design's hover state. */
function HoverButton({ style, hoverStyle, children, ...props }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...style, ...(hover && !props.disabled ? hoverStyle : null) }}
      {...props}
    >
      {children}
    </button>
  );
}

const READY_GREEN = "#22a55a";
const ERROR_RED = "#d32f2f";

// The badge over the icon's tip for the row's latest video; none while one is
// being made or before the first.
const BADGES = {
  done: {
    colour: READY_GREEN,
    note: "video ready",
    mark: svg(9, <path d="M20 6 9 17l-5-5" />, { strokeWidth: "3.5" }),
  },
  failed: {
    colour: ERROR_RED,
    note: "last video failed",
    mark: svg(
      9,
      <>
        <path d="M12 4v10" />
        <path d="M12 20h.01" />
      </>,
      { strokeWidth: "3.5" },
    ),
  },
};

/**
 * The icon on the workstation row, beside the AI Actions buttons. A ready
 * video gets a green ✓ over the icon's tip, a failed one a red !, drawn like
 * the grid's L / M sent badges. `status` is the row's latest video status.
 */
export function VideoActionButton({ onClick, status }) {
  const badge = BADGES[status];
  return (
    <HoverButton
      aria-label={badge ? `Upload dossier PDF, ${badge.note}` : "Upload dossier PDF"}
      title={badge ? `Upload dossier PDF · ${badge.note}` : "Upload dossier PDF"}
      onClick={onClick}
      style={{
        position: "relative",
        border: "none",
        background: "none",
        cursor: "pointer",
        padding: "2px",
        color: "#333",
        display: "flex",
        borderRadius: "4px",
      }}
      hoverStyle={{ background: ORANGE, color: "#fff" }}
    >
      <IconVideo size={20} />
      {badge && (
        <span
          style={{
            position: "absolute",
            top: "-4px",
            left: "16px",
            width: "13px",
            height: "13px",
            borderRadius: "50%",
            border: `1px solid ${badge.colour}`,
            background: badge.colour,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          {badge.mark}
        </span>
      )}
    </HoverButton>
  );
}

/** The overlay and white card every dialog here shares. */
function Dialog({ width, padding, centred, zIndex, onClose, children }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      <style>{`@keyframes pfade{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}`}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width,
          maxWidth: "100%",
          background: "#fff",
          borderRadius: "8px",
          boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
          padding,
          boxSizing: "border-box",
          animation: "pfade .2s ease-out",
          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
          ...(centred && {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }),
        }}
      >
        <HoverButton
          aria-label="Close"
          onClick={onClose}
          style={{
            position: "absolute",
            top: "14px",
            right: "14px",
            width: "22px",
            height: "22px",
            borderRadius: "5px",
            border: `1.5px solid ${ORANGE}`,
            background: "#fff",
            color: "#333",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
          }}
          hoverStyle={{ background: "#fff3ec" }}
        >
          {svg(12, <path d="M18 6 6 18M6 6l12 12" />, { strokeWidth: "2.5" })}
        </HoverButton>
        {children}
      </div>
    </div>
  );
}

const HEADING = { margin: 0, fontSize: "18px", fontWeight: 500, color: "#1f2937" };

const ACTION_BUTTON = {
  marginTop: "20px",
  height: "36px",
  padding: "0 18px",
  border: "none",
  background: ORANGE,
  borderRadius: "6px",
  fontSize: "13px",
  fontWeight: 500,
  fontFamily: "inherit",
  color: "#fff",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "6px",
};

const ROUND_ICON = {
  width: "44px",
  height: "44px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

/** What the row's latest video means for the icon. */
const viewFor = (job) => {
  if (!job || job.status === "failed") return "upload";
  return job.status === "done" ? "ready" : "submitted";
};

export default function CandidateVideo({ role_id, candidate, onClose }) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { token } = useOutletContext();
  const auth = { Authorization: `Bearer ${token}` };

  const [view, setView] = useState(null); // null while the row's state loads
  const [job, setJob] = useState(null);
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  // The user vouches for the file in hand; a different file needs vouching again.
  const [confirmed, setConfirmed] = useState(false);
  const inputRef = useRef(null);

  // Held in a ref: the grid passes a new function on every render, and the
  // status request below must run once per opening, not once per render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // The row's latest video decides which dialog opens.
  useEffect(() => {
    let live = true;
    axios
      .get(`${API_BASE_URL}/video/jobs`, {
        params: { role_id, candidate_id: candidate.candidate_id },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!live) return;
        const latest = res.data?.[0] || null;
        setJob(latest);
        setView(viewFor(latest));
        // A failed attempt says why in the upload box's error line.
        if (latest?.status === "failed") setError(latest.error || "");
      })
      .catch((err) => {
        if (!live) return;
        console.error("[video] status failed:", err.response?.data || err.message);
        toastr.error(err.response?.data?.detail || "Could not reach the video service.");
        onCloseRef.current();
      });
    return () => {
      live = false;
    };
  }, [API_BASE_URL, token, role_id, candidate.candidate_id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /** The design's checks, made before anything is sent. Node checks again. */
  const pick = (picked) => {
    if (!picked) return;
    const isPdf = picked.type === "application/pdf" || /\.pdf$/i.test(picked.name);
    if (!isPdf) {
      setFile(null);
      setError("Please choose a PDF file.");
      return;
    }
    if (picked.size > MAX_BYTES) {
      setFile(null);
      setError("File is larger than 20 MB.");
      return;
    }
    setFile(picked);
    setConfirmed(false);
    setError("");
  };

  const canSubmit = Boolean(file) && confirmed && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError("");
    const form = new FormData();
    form.append("file", file);
    form.append("role_id", role_id);
    form.append("candidate_id", candidate.candidate_id);
    try {
      const res = await axios.post(`${API_BASE_URL}/video/jobs`, form, { headers: auth });
      setJob(res.data);
      setView("submitted");
    } catch (err) {
      // 409: one is already being made for this row - that is "submitted" too.
      if (err.response?.status === 409) setView("submitted");
      else setError(err.response?.data?.detail || "Could not start the video.");
    } finally {
      setBusy(false);
    }
  };

  const fileName = `Dossier video — ${candidate.name || "candidate"}`;

  const download = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/video/jobs/${job.id}/video`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      // An anchor cannot send the Authorization header, so the file is fetched
      // first and handed to the browser as a blob.
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setDownloaded(true);
    } catch (err) {
      console.error("[video] download failed:", err.message);
      toastr.error("Could not download the video.");
    }
  }, [API_BASE_URL, token, job, fileName]);

  if (view === "upload") {
    return (
      <Dialog width="520px" padding="24px 26px 24px" zIndex={1050} onClose={onClose}>
        <h2 style={HEADING}>Upload Dossier</h2>
        <div style={{ marginTop: "6px", fontSize: "13px", color: "#6b7280" }}>
          Upload the dossier as a PDF file.
        </div>
        <input
          type="file"
          accept="application/pdf,.pdf"
          ref={inputRef}
          onChange={(e) => {
            pick(e.target.files[0]);
            e.target.value = "";
          }}
          style={{ display: "none" }}
        />
        <DropZone
          drag={drag}
          onClick={() => inputRef.current && inputRef.current.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (!drag) setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            pick(e.dataTransfer.files[0]);
          }}
        />
        {error && (
          <div style={{ marginTop: "10px", fontSize: "12px", color: "#d32f2f" }}>{error}</div>
        )}
        {file && (
          <div
            style={{
              marginTop: "12px",
              border: "1px solid #e6e6e8",
              borderRadius: "6px",
              padding: "10px 12px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            {svg(
              18,
              <>
                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
                <path d="M14 2v4a2 2 0 0 0 2 2h4" />
              </>,
              { stroke: "#d32f2f", strokeWidth: "1.8" },
            )}
            <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
              <span
                style={{
                  fontSize: "13px",
                  color: "#222",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {file.name}
              </span>
              <span style={{ fontSize: "11px", color: "#6b7280" }}>
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </span>
            </div>
            <HoverButton
              aria-label="Remove file"
              onClick={() => {
                setFile(null);
                setConfirmed(false);
              }}
              style={{
                border: "none",
                background: "none",
                cursor: "pointer",
                padding: "4px",
                color: "#d32f2f",
                display: "flex",
              }}
              hoverStyle={{ color: "#9a1c1c" }}
            >
              <IconTrash />
            </HoverButton>
          </div>
        )}
        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "8px",
            marginTop: "14px",
            fontSize: "13px",
            color: file ? "#222" : "#9ca3af",
            cursor: file ? "pointer" : "default",
          }}
        >
          <input
            type="checkbox"
            checked={confirmed}
            disabled={!file}
            onChange={(e) => setConfirmed(e.target.checked)}
            style={{ marginTop: "2px", accentColor: ORANGE, cursor: "inherit" }}
          />
          <span>
            I confirm this is {candidate.name || "this candidate"}'s dossier and its
            details are correct.
          </span>
        </label>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "20px" }}>
          <HoverButton
            onClick={onClose}
            style={{
              height: "34px",
              padding: "0 14px",
              border: "1px solid #e0e0e0",
              background: "#fff",
              borderRadius: "6px",
              fontSize: "13px",
              fontWeight: 500,
              fontFamily: "inherit",
              color: "#222",
              cursor: "pointer",
            }}
            hoverStyle={{ background: "#f5f5f5" }}
          >
            Cancel
          </HoverButton>
          <HoverButton
            onClick={submit}
            disabled={!canSubmit}
            style={{
              height: "34px",
              padding: "0 16px",
              border: "none",
              background: ORANGE,
              borderRadius: "6px",
              fontSize: "13px",
              fontWeight: 500,
              fontFamily: "inherit",
              color: "#fff",
              cursor: canSubmit ? "pointer" : "default",
              opacity: canSubmit ? 1 : 0.45,
            }}
            hoverStyle={{ background: ORANGE_DARK }}
          >
            Generate Video
          </HoverButton>
        </div>
      </Dialog>
    );
  }

  if (view === "submitted") {
    return (
      <Dialog width="420px" padding="28px 26px 24px" centred zIndex={1060} onClose={onClose}>
        <div style={{ ...ROUND_ICON, background: "#e9f7ef" }}>
          {svg(22, <path d="M20 6 9 17l-5-5" />, { stroke: "#22a55a", strokeWidth: "2.5" })}
        </div>
        <h2 style={{ ...HEADING, margin: "14px 0 0" }}>Request submitted</h2>
        <div
          style={{
            marginTop: "6px",
            fontSize: "13px",
            lineHeight: 1.5,
            color: "#6b7280",
            textWrap: "pretty",
          }}
        >
          Your video is being generated. Click the video icon again to download it.
        </div>
        <HoverButton onClick={onClose} style={ACTION_BUTTON} hoverStyle={{ background: ORANGE_DARK }}>
          Close
        </HoverButton>
      </Dialog>
    );
  }

  if (view === "ready") {
    return (
      <Dialog width="420px" padding="28px 26px 24px" centred zIndex={1060} onClose={onClose}>
        <div style={{ ...ROUND_ICON, background: "#fff3ec", color: ORANGE }}>
          <IconVideo size={22} />
        </div>
        <h2 style={{ ...HEADING, margin: "14px 0 0" }}>Your video is ready</h2>
        <div style={{ marginTop: "6px", fontSize: "13px", color: "#6b7280" }}>{fileName}.mp4</div>
        <HoverButton onClick={download} style={ACTION_BUTTON} hoverStyle={{ background: ORANGE_DARK }}>
          {svg(
            14,
            <>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="m7 10 5 5 5-5" />
              <path d="M12 15V3" />
            </>,
          )}
          {downloaded ? "Download started" : "Download Video"}
        </HoverButton>
      </Dialog>
    );
  }

  return null; // still asking Node which dialog to open
}

/** "Click to upload or drag and drop" - its border follows hover and drag. */
function DropZone({ drag, onClick, onDragOver, onDragLeave, onDrop }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        marginTop: "18px",
        border: `1.5px dashed ${drag || hover ? ORANGE : "#c9c9c9"}`,
        background: drag ? "#fff3ec" : "#fafafa",
        borderRadius: "8px",
        padding: "28px 16px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
        cursor: "pointer",
        textAlign: "center",
      }}
    >
      {svg(
        22,
        <>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <path d="M17 8l-5-5-5 5" />
          <path d="M12 3v12" />
        </>,
        { stroke: ORANGE, strokeWidth: "1.8" },
      )}
      <div style={{ fontSize: "13px", color: "#222" }}>
        <span style={{ color: ORANGE, fontWeight: 500 }}>Click to upload</span> or drag and drop
      </div>
      <div style={{ fontSize: "11px", color: "#6b7280" }}>PDF only · max 20 MB</div>
    </div>
  );
}
